import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../components/AuthContext';

export default function TopUp() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [credits, setCredits] = useState(user?.credits ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [topups, setTopups] = useState([]);

  const params = new URLSearchParams(location.search);
  const success   = params.get('success') === 'true';
  const cancelled = params.get('cancelled') === 'true';

  useEffect(() => {
    // Refresh credits from server (especially after returning from Stripe)
    api.payments.credits()
      .then(data => {
        setCredits(data.credits);
        // Update stored user object
        if (user) {
          login(localStorage.getItem('txtmycar_token'), { ...user, credits: data.credits });
        }
      })
      .catch(console.error);

    api.payments.history()
      .then(setTopups)
      .catch(console.error);
  }, []);

  const handleTopUp = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await api.payments.createCheckout();
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Top Up Credits</h1>
        <p className="page-subtitle">Credits are used to send messages — 5 credits per SMS</p>
      </div>

      {success && (
        <div className="alert alert-success" style={{ marginBottom: 24 }}>
          Payment successful! Your credits have been added to your account.
        </div>
      )}
      {cancelled && (
        <div className="alert alert-error" style={{ marginBottom: 24 }}>
          Payment cancelled — no charge was made.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>

        {/* Current balance */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Current balance
          </h2>
          <div style={{ fontSize: 64, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: credits > 0 ? 'var(--accent)' : 'var(--danger)', letterSpacing: -2, lineHeight: 1, marginBottom: 8 }}>
            {credits}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>
            {credits >= 5
              ? `Enough for ${Math.floor(credits / 5)} message${Math.floor(credits / 5) !== 1 ? 's' : ''}`
              : 'Not enough credits to send a message'}
          </div>
        </div>

        {/* Top up */}
        <div className="card">
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Top up
          </h2>
          <div style={{ fontSize: 32, fontWeight: 800, fontFamily: 'Syne, sans-serif', marginBottom: 4 }}>
            £10.00
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
            100 credits — enough for 20 messages
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <button className="btn btn-primary" onClick={handleTopUp} disabled={loading}>
            {loading ? <><span className="spinner"></span> Redirecting to payment...</> : 'Pay with card — £10'}
          </button>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, textAlign: 'center' }}>
            Secure payment via Stripe. Credits added instantly.
          </p>
        </div>
      </div>

      {/* Top-up history */}
      <div className="card">
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Payment history</h2>
        {topups.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>No payments yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>Credits</th>
              </tr>
            </thead>
            <tbody>
              {topups.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--bg3)' }}>
                  <td style={{ padding: '10px 0', fontSize: 14 }}>{formatDate(t.created_at)}</td>
                  <td style={{ padding: '10px 0', fontSize: 14 }}>£{(t.amount_pence / 100).toFixed(2)}</td>
                  <td style={{ padding: '10px 0', fontSize: 14, color: 'var(--accent)', fontWeight: 700 }}>+{t.credits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

